import { api } from './api';

// Types
export interface PaymentMethodTotals {
  CASH: number;
  CARD: number;
  TRANSFER: number;
  CHECK: number;
  TOTAL: number;
}

export interface CashDrawerSession {
  id: string;
  locationId: string;
  userId: string;
  openTime: string;
  closeTime?: string;
  openingBalance: number;
  closingBalance?: number;
  expectedBalance?: number;
  difference?: number;
  status: 'OPEN' | 'CLOSED';
  notes?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  location: {
    id: string;
    name: string;
  };
  transactions: CashDrawerTransaction[];
  paymentMethodTotals?: PaymentMethodTotals;
}

export interface CashDrawerTransaction {
  id: string;
  sessionId: string;
  type: 'OPENING' | 'SALE' | 'ADJUSTMENT' | 'WITHDRAWAL' | 'DEPOSIT' | 'CLOSING';
  amount: number;
  description: string;
  createdAt: string;
  sale?: {
    id: string;
    customerName?: string;
    total: number;
    paymentMethod: string;
    createdAt: string;
    saleItems?: {
      id: string;
      quantity: number;
      unitPrice: number;
      total: number;
      product?: {
        name: string;
        sku: string;
      };
    }[];
  };
}

export interface CashDrawerSessionResponse {
  success: boolean;
  data: CashDrawerSession;
  message?: string;
}

export interface CashDrawerSessionsResponse {
  success: boolean;
  data: CashDrawerSession[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CashTransactionResponse {
  success: boolean;
  data: CashDrawerTransaction;
  message?: string;
}

export interface OpenCashDrawerData {
  locationId: string;
  openingBalance: number;
}

export interface CloseCashDrawerData {
  closingBalance: number;
  notes?: string;
}

export interface CashTransactionData {
  amount: number;
  description: string;
}

export const cashDrawerApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Obtener sesión activa
    getActiveSession: builder.query<CashDrawerSessionResponse, string>({
      query: (locationId) => ({
        url: `/cash-drawer/active/${locationId}`,
        method: 'GET',
      }),
      providesTags: ['CashDrawer'],
    }),

    // Abrir caja registradora
    openCashDrawer: builder.mutation<CashDrawerSessionResponse, OpenCashDrawerData>({
      query: (data) => ({
        url: '/cash-drawer/open',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CashDrawer'],
    }),

    // Cerrar caja registradora
    closeCashDrawer: builder.mutation<CashDrawerSessionResponse, { sessionId: string; data: CloseCashDrawerData }>({
      query: ({ sessionId, data }) => ({
        url: `/cash-drawer/close/${sessionId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CashDrawer'],
    }),

    // Crear ajuste
    createAdjustment: builder.mutation<CashTransactionResponse, { sessionId: string; data: CashTransactionData }>({
      query: ({ sessionId, data }) => ({
        url: `/cash-drawer/adjustment/${sessionId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CashDrawer'],
    }),

    // Crear retiro
    createWithdrawal: builder.mutation<CashTransactionResponse, { sessionId: string; data: CashTransactionData }>({
      query: ({ sessionId, data }) => ({
        url: `/cash-drawer/withdrawal/${sessionId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CashDrawer'],
    }),

    // Crear depósito
    createDeposit: builder.mutation<CashTransactionResponse, { sessionId: string; data: CashTransactionData }>({
      query: ({ sessionId, data }) => ({
        url: `/cash-drawer/deposit/${sessionId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CashDrawer'],
    }),

    // Obtener historial de sesiones
    getSessionHistory: builder.query<CashDrawerSessionsResponse, {
      locationId: string;
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
      userId?: string;
    }>({
      query: ({ locationId, page = 1, limit = 10, startDate, endDate, userId }) => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (userId) params.append('userId', userId);

        return {
          url: `/cash-drawer/history/${locationId}?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['CashDrawer'],
    }),

    // Obtener detalles de una sesión específica
    getSessionDetails: builder.query<CashDrawerSessionResponse, string>({
      query: (sessionId) => ({
        url: `/cash-drawer/session/${sessionId}`,
        method: 'GET',
      }),
      providesTags: (result, error, sessionId) => [
        { type: 'CashDrawer', id: sessionId }
      ],
    }),
  }),
});

export const {
  useGetActiveSessionQuery,
  useOpenCashDrawerMutation,
  useCloseCashDrawerMutation,
  useCreateAdjustmentMutation,
  useCreateWithdrawalMutation,
  useCreateDepositMutation,
  useGetSessionHistoryQuery,
  useGetSessionDetailsQuery,
} = cashDrawerApi;